import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { IProvider } from 'src/types';
import { setToLocalStorage, getFromLocalStorage } from 'src/utils';
import { satsToBtc } from 'src/utils/raffleHelpers';
import type { Raffle } from 'src/types/raffle';

const CART_STORAGE_KEY = '::@@omegaoneCartState@@::';

export interface CartItem {
  raffleId: string;
  raffle: Raffle;
  quantity: number;
}

interface ICartContext {
  items: CartItem[];
  addToCart: (raffle: Raffle, quantity: number) => void;
  removeFromCart: (raffleId: string) => void;
  updateQuantity: (raffleId: string, quantity: number) => void;
  clearCart: () => void;
  getTotalBtc: () => number;
  getItemCount: () => number;
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

const CartContext = createContext<ICartContext>({
  items: [],
  addToCart: noop,
  removeFromCart: noop,
  updateQuantity: noop,
  clearCart: noop,
  getTotalBtc: () => 0,
  getItemCount: () => 0,
});

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }: IProvider) => {
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const stored = getFromLocalStorage(CART_STORAGE_KEY);
      if (stored) return JSON.parse(stored) as CartItem[];
    } catch {
      // ignore parse errors
    }
    return [];
  });

  useEffect(() => {
    setToLocalStorage(CART_STORAGE_KEY, items);
  }, [items]);

  const addToCart = useCallback((raffle: Raffle, quantity: number) => {
    setItems(prev => {
      const existing = prev.find(item => item.raffleId === raffle.id);
      if (existing) {
        return prev.map(item =>
          item.raffleId === raffle.id
            ? { ...item, quantity: item.quantity + quantity }
            : item,
        );
      }
      return [...prev, { raffleId: raffle.id, raffle, quantity }];
    });
  }, []);

  const removeFromCart = useCallback((raffleId: string) => {
    setItems(prev => prev.filter(item => item.raffleId !== raffleId));
  }, []);

  const updateQuantity = useCallback((raffleId: string, quantity: number) => {
    if (quantity <= 0) {
      setItems(prev => prev.filter(item => item.raffleId !== raffleId));
      return;
    }
    setItems(prev =>
      prev.map(item => (item.raffleId === raffleId ? { ...item, quantity } : item)),
    );
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const getTotalBtc = useCallback(() => {
    return items.reduce(
      (total, item) => total + satsToBtc(item.raffle.ticketPriceSats) * item.quantity,
      0,
    );
  }, [items]);

  const getItemCount = useCallback(() => {
    return items.reduce((total, item) => total + item.quantity, 0);
  }, [items]);

  return (
    <CartContext.Provider
      value={{ items, addToCart, removeFromCart, updateQuantity, clearCart, getTotalBtc, getItemCount }}
    >
      {children}
    </CartContext.Provider>
  );
};
